const swea = require('swea');
import * as globals from '../globals';
const EyeDropperInteraction = swea.editor.interactions.EyeDropperInteraction;
const ContextMenuStateServiceActions = swea.react.actions.ContextMenuStateServiceActions;
const InstructionServiceActions = swea.react.actions.InstructionServiceActions;
const DialogServiceActions = swea.react.actions.DialogServiceActions;
const DialogService = swea.react.services.DialogService;
const ViewerManager = swea.editor.ViewerManager;
const InstructionService = swea.react.services.InstructionService;
const ImageViewerService = swea.react.services.ImageViewerService;
const ImageViewerServiceActions = swea.react.actions.ImageViewerServiceActions;
const util = swea.util;
const BrowserInfo = swea.react.helpers.BrowserInfo;
const InteractionManager = swea.editor.InteractionManager;
const InspectionAdapter = swea.editor.InspectionAdapter;


class CustomEyeDropperInteraction extends EyeDropperInteraction {
  /**
   * Number of chars to trim from camera names from the left.
   * @type { number }
   * @static
   * @private
   */  
  static TRIM_CHARS = 24;
  
  /**
   * Constructs the interaction.
   * @param { object } options An option object, see {@link Interaction} for more info.
   * @param { boolean } options.demo (optional) True for demo status, e.g. not showing temperature/image. Default is false.
   * @param { editor.interactions.ImageSelectionInteraction } imageSelectionInteraction The image selection interaction.
   * @param { boolean } options.showDownload (optional) False to disable download image icon. Default is true.
   * @param { boolean } options.forceOverlayViewer (optional) True to force setting overlay viewer even if mobile, to update e.g. compass and altitude. Default is false.
   * @public
   */  
  constructor(options = {}) {
    options.radiometricOnly = true;
    super(options);
    
    this._dialogListener = undefined;
    this._demo = (options.demo === true);
    this._imageSelectionInteraction = options.imageSelectionInteraction;
    this._imageNames = [];
    this._showDownload = (options.showDownload === undefined ? true : options.showDownload);
    this._forceOverlayViewer = (options.forceOverlayViewer === undefined ? false : options.forceOverlayViewer);
    this._disabledInteractionClasses = [];
  }
  
  /**
   * Initializes the interaction.
   * @param element the element that the interaction listens to.
   * @param viewerId the viewer id of the viewer the interaction operates in.
   */
  initialize(element, viewerId){
    super.initialize(element, viewerId);

    // When user closes the tool
    this.addTrigger(new swea.editor.interactions.EventHandler(
      [swea.editor.interactions.eventCodes.ESC],
      this,
      () => {
        this.terminate();
      }
    ));
  }

  /**
   * Called before eye-drop.
   * @param { number } viewerId The ID of the viewer.
   * @private
   * @override
   */
  _beforeEyeDrop(viewerId) {
    InstructionServiceActions.showInstruction('instructions.eyedropping');
    ViewerManager.instance().getViewerById(viewerId).setCursor('wait');
  }

  /**
   * Called after delta reference eye-drop. Meant to be overridden.
   * @param { number } viewerId The ID of the viewer.
   * @param { object } valuesPerImage (optional) Image names mapped to eye-dropped values as arrays of r,g,b,a (0-255).
   * @private
   * @override
   */
  _whenReferenceEyeDropped(viewerId, valuesPerImage) {
    viewerId;
    valuesPerImage;
    InstructionServiceActions.showInstruction('instructions.eyedropper_delta2');
  }
  
  /**
   * Called after eye-drop. Meant to be overridden.
   * @param { number } viewerId The ID of the viewer.
   * @param { object } valuesPerImage (optional) Image names mapped to eye-dropped values or deltas as arrays of r,g,b,a (0-255).
   * @param { object } anglesPerImage (optional) Image names mapped to the vertical camera angles in radians.
   * @param { object } stats (optional) A statistics object containing value (used for single value, otherwise undefined), minValue, maxValue and avgValue as arrays of r,g,b (0-255).
   * @param { boolean } refined (optional) True if refined value.
   * @private
   * @override
   */
  _whenEyeDropped(viewerId, valuesPerImage, anglesPerImage, stats, refined) {
    InstructionServiceActions.hideInstruction();
    
    if (valuesPerImage !== undefined && Object.keys(valuesPerImage).length !== 0) {
      this._imageNames = Object.keys(valuesPerImage);
      
      // Sort per value
      const sortedArrays = this._imageNames.map(function(imageName) {
        return [imageName, valuesPerImage[imageName] === undefined ? undefined : valuesPerImage[imageName][0], anglesPerImage === undefined ? 0.0 : anglesPerImage[imageName] || 0.0];
      });
      sortedArrays.sort(function(first, second) {
        return (first[1] || 0) - (second[1] || 0);
      });
      const num = sortedArrays.length;
      if (num > 0) {
        let dialogText = refined === true ? '(' + swea.i18n.t('components.eyedropper.refined') + ')<br />' : '';
        dialogText += '<table><tbody><tr><th>Name</th><th>' + (this._delta ? 'Temp<br />diff' : 'Temp') + '</th>' + (!this._delta ? '<th>Outdoor<br />diff</th>' : '') + '</tr>';
        
        let temperatureSum = 0.0;
        const temperatures = [];
        const matchingTemperatures = [];
        let numMatching = 0, numNonMatching = 0;
        for (let i = 0; i < num; ++i) {
          const element = sortedArrays[i];
          const imageName = element[0];
          const value = element[1];
          const angle = element[2];
          const originalName = ImageViewerService.instance().getOriginalImageName(imageName);
          const cameraName = (originalName !== '' ? originalName : imageName);
          
          let outdoorTemp = undefined;
          const camera = InspectionAdapter.getCameraByName(imageName);
          if (camera !== undefined && camera.thermalEnvironment !== undefined) {
            outdoorTemp = camera.thermalEnvironment.temperature;
            if (isNaN(outdoorTemp)) {
              outdoorTemp = undefined;
            }
          }
          
          if (value !== undefined) {
            dialogText += '<tr>';
            // NOTE: Avoid rounding like before, to get better understanding of statistics
            const temperature = value; //util.round(value, 0);
            temperatures.push(temperature);
            if (ImageViewerService.instance().getState().isOpen) {
              // No image links in image view
              dialogText += '<td>' + util.trimFromLeft(cameraName, CustomEyeDropperInteraction.TRIM_CHARS, true) + '</td>';
            }
            else {
              dialogText += '<td><a href="' + imageName + '">' + util.trimFromLeft(cameraName, CustomEyeDropperInteraction.TRIM_CHARS, true) + '</a></td>';
            }
            const roundedTemp = util.round(temperature, EyeDropperInteraction.ACCURACY);
            dialogText += '<td>' + (roundedTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(roundedTemp) : roundedTemp) + '°C</td>';
            // TODO: angle
            angle;
            //dialogText += ' (' + util.round(Math.abs(angle * 180.0 / Math.PI), 0) + '°)';
            
            if (!this._delta) {
              const camera = InspectionAdapter.getCameraByName(imageName);
              if (outdoorTemp !== undefined) {
                const outdoorTempDiff = util.round(temperature - camera.thermalEnvironment.temperature, EyeDropperInteraction.ACCURACY);
                dialogText += '<td>' + (outdoorTempDiff > 0 ? '+' : '') + outdoorTempDiff + '°C</td>';
              }
              else {
                dialogText += '<td>n/a</td>';
              }
            }
            
            dialogText += '</tr>';
            temperatureSum += temperature;
            ++numMatching;
            matchingTemperatures.push(temperature);
          }
          else {
            ++numNonMatching;
          }
        }
        dialogText += '</tbody></table>';
        
        if (this._delta && numNonMatching > 0) {
          dialogText += '<br />' + numNonMatching + ' ' + swea.i18n.t('components.eyedropper.nonMatching') + '<br />';
        }
        
        if (numMatching > 1) {
          const avgTemp = util.round(this._delta ? temperatureSum / numMatching : temperatureSum / numMatching, EyeDropperInteraction.ACCURACY);
          dialogText += ('<br />' + swea.i18n.t('components.eyedropper.average') + ': ' + (avgTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(avgTemp) : avgTemp) + '°C<br />');
        }
        if (matchingTemperatures.length > 1) {
          if (this._delta) {
            dialogText = matchingTemperatures.length + ' ' + swea.i18n.t('components.eyedropper.matching') + ':<br />' + dialogText;
          }
          const medianIndex = (matchingTemperatures.length - 1) / 2.0;
          let medianTemperature = 0.0;
          if (num > 1 && Math.floor(medianIndex) != medianIndex) {
            medianTemperature = (matchingTemperatures[Math.floor(medianIndex)] + matchingTemperatures[Math.ceil(medianIndex)]) / 2.0;
          }
          else {
            medianTemperature = matchingTemperatures[medianIndex];
          }
          const roundedMedTemp = util.round(this._delta ? medianTemperature : medianTemperature, EyeDropperInteraction.ACCURACY);
          dialogText += (swea.i18n.t('components.eyedropper.median') + ': ' + (roundedMedTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(roundedMedTemp) : roundedMedTemp) + '°C<br />');
        }
        
        DialogServiceActions.open(this._delta ? swea.i18n.t('components.eyedropper.deltaTemperatures') : swea.i18n.t('components.eyedropper.temperatures'), dialogText, DialogService.AlignmentType.RIGHT, false, false, '', '', false, '', '', false, true, this._clickedLink);
      }
    }
    else if (stats !== undefined) {
      const imageName = ImageViewerService.instance().getState().imageNames[ImageViewerService.instance().getState().currentImageIndex];
      const originalName = ImageViewerService.instance().getOriginalImageName(imageName);
      const cameraName = (originalName !== '' ? originalName : imageName);

      let outdoorTemp = undefined;
      const camera = InspectionAdapter.getCameraByName(imageName);
      if (camera !== undefined && camera.thermalEnvironment !== undefined) {
        outdoorTemp = camera.thermalEnvironment.temperature;
        if (isNaN(outdoorTemp)) {
          outdoorTemp = undefined;
        }
      }
      
      const minTemp = util.round(stats.minValue[0], EyeDropperInteraction.ACCURACY);
      const minTempOutdoorDiff = (outdoorTemp !== undefined ? util.round(stats.minValue[0] - outdoorTemp, EyeDropperInteraction.ACCURACY) : undefined);
      const maxTemp = util.round(stats.maxValue[0], EyeDropperInteraction.ACCURACY);
      const maxTempOutdoorDiff = (outdoorTemp !== undefined ? util.round(stats.maxValue[0] - outdoorTemp, EyeDropperInteraction.ACCURACY) : undefined);
      const avgTemp = util.round(stats.avgValue[0], EyeDropperInteraction.ACCURACY);
      const avgTempOutdoorDiff = (outdoorTemp !== undefined ? util.round(stats.avgValue[0] - outdoorTemp, EyeDropperInteraction.ACCURACY) : undefined);
      const medTemp = util.round(stats.medValue[0], EyeDropperInteraction.ACCURACY);
      const medTempOutdoorDiff = (outdoorTemp !== undefined ? util.round(stats.medValue[0] - outdoorTemp, EyeDropperInteraction.ACCURACY) : undefined);
      const diffTemp = util.round(stats.maxValue[0] - stats.minValue[0], EyeDropperInteraction.ACCURACY);

      let dialogText = refined === true ? '(' + swea.i18n.t('components.eyedropper.refined') + ')<br />' : '';
      dialogText += util.trimFromLeft(cameraName, CustomEyeDropperInteraction.TRIM_CHARS, true) + '<br />';
      dialogText += '<table><tbody><tr><th>Stat</th><th>Temp</th><th>Outdoor<br />diff</th></tr>';
      dialogText += '<tr><td>' + swea.i18n.t('components.eyedropper.min') + '</td><td>' + (minTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(minTemp) : minTemp) + '°C</td><td>' + (minTempOutdoorDiff !== undefined ? minTempOutdoorDiff + '°C' : 'n/a') + '</td></tr>';
      dialogText += '<tr><td>' + swea.i18n.t('components.eyedropper.max') + '</td><td>' + (maxTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(maxTemp) : maxTemp) + '°C</td><td>' + (maxTempOutdoorDiff !== undefined ? maxTempOutdoorDiff + '°C' : 'n/a') + '</td></tr>';
      dialogText += '<tr><td>' + swea.i18n.t('components.eyedropper.average') + '</td><td>' + (avgTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(avgTemp) : avgTemp) + '°C</td><td>' + (avgTempOutdoorDiff !== undefined ? avgTempOutdoorDiff + '°C' : 'n/a') + '</td></tr>';
      dialogText += '<tr><td>' + swea.i18n.t('components.eyedropper.median') + '</td><td>' + (medTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(medTemp) : medTemp) + '°C</td><td>' + (medTempOutdoorDiff !== undefined ? medTempOutdoorDiff + '°C' : 'n/a') + '</td></tr>';
      dialogText += '<tr><td>' + swea.i18n.t('components.eyedropper.diff') + '</td><td>' + (diffTemp > 0 && !this._delta ? '+' : '') + (this._delta ? Math.abs(diffTemp) : diffTemp) + '°C</td><td>n/a</td></tr>';
      dialogText += '</tbody></table>';
      DialogServiceActions.open(swea.i18n.t('components.eyedropper.temperatures'), dialogText, DialogService.AlignmentType.RIGHT, false, false, '', '', false, '', '', false, true, this._clickedLink);
    }
    else {
      InstructionServiceActions.showInstruction('instructions.failed_eyedropping');
      DialogServiceActions.close();
    }

    if (!this.isTerminated()) {
      ViewerManager.instance().getViewerById(viewerId).setCursor('crosshair');
    }
  }
  
  /**
   * Called when user clicks one of the image names.
   * @param { string } firstImageName The name of the image clicked.
   * @private
   */
  _clickedLink = (firstImageName) => {
    // Show all images in image viewer, with the current first
    DialogServiceActions.close();
    
    const viewerNames = [firstImageName];
    const numImageNames = this._imageNames.length;
    for (let i = 0; i < numImageNames; ++i) {
      const imageName = this._imageNames[i].trim();
      if (imageName !== firstImageName) {
        viewerNames.push(imageName);
      }
    }
    
    if (this._imageSelectionInteraction && this._imageSelectionInteraction.setReopen !== undefined) {
      this._imageSelectionInteraction.setReopen(false);
    }
    
    const overlayViewer = (BrowserInfo.isMobile() && !this._forceOverlayViewer ? undefined : ViewerManager.instance().getViewerById(this._viewerId));
    ImageViewerServiceActions.open({
      imageNames: viewerNames,
      overlayViewer: overlayViewer,
      showDownload: this._showDownload,
      enableFavorites: false
    });
  };
  
  /**
   * Sets first instruction text, depending on delta status and image viewer.
   * @private
   */
  _setInstruction() {
    let instruction = 'instructions.eyedropper';
    if (this._delta) {
      instruction = 'instructions.eyedropper_delta';
    }
    else {
      instruction = ImageViewerService.instance().getState().isOpen ? 'instructions.eyedropper2d' : 'instructions.eyedropper';
    }

    InstructionServiceActions.showInstruction(instruction);
  }
  
  /**
   * When the interaction is initialized open the correct context menu.
   * @param element { DOMElement } The element that the interaction works on
   * @param viewerId { number } The ID of the viewer.
   * @override
   */
  whenInitialized(element, viewerId){
    super.whenInitialized(element, viewerId);
    
    ContextMenuStateServiceActions.openContextMenu(
      globals.LOWER_CONTEXT_MENU,
      globals.EYE_DROPPER_ACTIVATED
    );

    this._setInstruction();
    ViewerManager.instance().getViewerById(viewerId).setCursor('crosshair');
    
    this._disabledInteractionClasses = InteractionManager.instance().disableContinuousInteractions();
    
    this._dialogListener = DialogService.instance().addChangeListener(this._onDialogChange);
  }

  /**
   * Hook that is called after termination.
   * This method can be overridden for custom termination behaviour.
   * @param { Element } element The DOM element.
   * @param { number } viewerId The viewer id.
   * @public
   * @override
   */
  whenTerminated(element, viewerId){
    super.whenTerminated(element, viewerId);
    ContextMenuStateServiceActions.closeContextMenu(
      globals.LOWER_CONTEXT_MENU
    );
    
    InstructionServiceActions.hideInstruction();
    ViewerManager.instance().getViewerById(viewerId).setCursor('default');

    InteractionManager.instance().enableContinuousInteractions(this._disabledInteractionClasses);
    
    DialogService.instance().removeChangeListener(this._dialogListener);
  }
  
  /**
   * Called when image has changed, overridden to change cursor.
   * @override
   * @private
   */
  _imageChanged() {
    if (!this.isTerminated()) {
      // Reset to crosshair
      ViewerManager.instance().getViewerById(this._viewerId).setCursor('crosshair');
      
      // Turn off eyedropper position and close dialog, as it's not correct anymore
      this._removeSpriteModels();
      DialogServiceActions.close();
    }
  }
  
  /**
   * Called when dialog has changed.
   * @private
   */
  _onDialogChange = () => {
    if (!DialogService.instance().getState().isOpen) {
      const currentText = InstructionService.instance().getState().text;
      if (!InstructionService.instance().getState().isOpen || currentText != swea.i18n.t('instructions.failed_eyedropping')) {
        this._setInstruction();
      }
      if (!this.isTerminated()) {
        this.getViewer().setCursor('crosshair');
      }
    }
  };
}
export default CustomEyeDropperInteraction;
